/* eslint-disable prefer-const */
import { PayloadAction } from "@reduxjs/toolkit";
import { closeAppBackdrop, notify, showAppBackdrop } from "features/app/store/slice";
import { fetchDataUser } from "features/user-management/store/slice";
import { call, put, select, takeEvery } from "redux-saga/effects";
import { IApiResponse } from "types/api";
import { formatPath, getQueryString } from "utils";
import { apiGet, apiPost } from "utils/api";
import CrawlingManagementPaths from "../models/paths";
import { addNewURL, addNewURLSuccess, deleteSummary, deleteSummarySuccess, deleteURL, deleteURLSuccess, fetchCategory, fetchCategorySuccess, fetchCrawlingURL, fetchCrawlingURLSuccess, fetchDetailURL, fetchDetailURLSuccess, fetchFrequency, fetchFrequencySuccess, getCrawlingData, updateFrequency, updateFrequencySuccess, updateURL, updateURLSuccess } from "./slice";
import { ICrawlingURLData } from "types/models/crawling-url";

const apiFetchCrawlingData = async (body: any) => {
  const queryString = getQueryString({ ...body });
  return apiGet<any>(formatPath(CrawlingManagementPaths.get_url, queryString));
};

export function* handleFetchCrawlingData(action: PayloadAction<any>) {
  yield put(showAppBackdrop());
  try {
    const response: IApiResponse<any> = yield call(apiFetchCrawlingData, action.payload);
    if (response.data) {
      yield put(fetchCrawlingURLSuccess(response.data.data));
      yield put(fetchDataUser());
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

const apiFetchFrequency = async () => {
  return apiGet<any>(
    formatPath(CrawlingManagementPaths.get_frequency)
  );
};

export function* handleFetchFrequency() {
  try {
    const response: IApiResponse<any> = yield call(apiFetchFrequency);
    if (response.data) {
      yield put(fetchFrequencySuccess(response.data));
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
    }
  } catch (e) {
  }
}

const apiFetchCategory = async () => {
  return apiGet<any>(
    formatPath(CrawlingManagementPaths.get_category)
  );
};

export function* handleFetchCategory() {
  try {
    const response: IApiResponse<any> = yield call(apiFetchCategory);
    if (response.data) {
      yield put(fetchCategorySuccess(response.data));
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
    }
  } catch (e) {
  }
}

const apiFetchDetailURL = async (url_id: any) => {
  const queryString = getQueryString({ url_id });
  return apiGet<any>(
    formatPath(CrawlingManagementPaths.get_detail_urls, queryString)
  );
};

export function* handleFetchDetailURL(action: PayloadAction<any>) {
  yield put(showAppBackdrop());
  try {
    const response: IApiResponse<any> = yield call(apiFetchDetailURL, action.payload);
    if (response.data) {
      yield put(fetchDetailURLSuccess(response.data.detail_url));
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

const apiAddNewURL = async (body: any) => {
  return apiPost<any>(
    formatPath(CrawlingManagementPaths.add_url), body
  );
};

function* handleAddNewURL(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const params: ICrawlingURLData = yield select(getCrawlingData)
    const response: IApiResponse<any> = yield call(apiAddNewURL, {
      ...action.payload,
      current_page: params.current_page,
      limit_per_page: params.limit_per_page
    });
    if (response.data) {
      yield put(addNewURLSuccess(response.data.data));
      yield put(
        notify(`This URl was successfully added`, {
          options: { variant: 'success' },
        })
      );
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

const apiUpdateURL = async (body: any) => {
  return apiPost<any>(
    formatPath(CrawlingManagementPaths.edit_url), body
  );
};

function* handleUpdateURL(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const params: ICrawlingURLData = yield select(getCrawlingData)
    const response: IApiResponse<any> = yield call(apiUpdateURL, {
      ...action.payload,
      current_page: params.current_page,
      limit_per_page: params.limit_per_page
    });
    if (response.data) {
      yield put(updateURLSuccess(response.data.data));
      yield put(
        notify(`This URL has been updated successfully`, {
          options: { variant: 'success' },
        })
      );
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

const apiDeleteURL = async (body: any) => {
  return apiPost<any>(
    formatPath(CrawlingManagementPaths.delete_url), body
  );
};

function* handleDeleteURL(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const params: ICrawlingURLData = yield select(getCrawlingData)
    const response: IApiResponse<any> = yield call(apiDeleteURL, {
      ...action.payload,
      current_page: params.current_page,
      limit_per_page: params.limit_per_page
    });
    if (response.data) {
      yield put(deleteURLSuccess(response.data.data));
      yield put(
        notify(`This URL deleted successfully`, {
          options: { variant: 'success' },
        })
      );
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

const apiDeleteSummary = async (body: any) => {
  return apiPost<any>(
    formatPath(CrawlingManagementPaths.delete_summary), body
  );
};

function* handleDeleteSummaryCrawling(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiDeleteSummary, {
      ...action.payload
    });
    if (response.data) {
      yield put(deleteSummarySuccess(response.data.data));
      yield put(
        notify(`Successful delete crawling's record`, {
          options: { variant: 'success' },
        })
      );
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

const apiUpdateFrequency = async (body: any) => {
  return apiPost<any>(
    formatPath(CrawlingManagementPaths.set_frequency), body
  );
};

function* handleUpdateFrequency(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiUpdateFrequency, {
      frequency_update: action.payload
    });
    if (response.data) {
      yield put(updateFrequencySuccess(action.payload));
      yield put(
        notify(`${response.data.detail}`, {
          options: { variant: 'success' },
        })
      );
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== '') {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: 'error' },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export default function* CrawlingURLSaga() {
  yield takeEvery(fetchCrawlingURL.type, handleFetchCrawlingData);
  yield takeEvery(addNewURL.type, handleAddNewURL);
  yield takeEvery(updateURL.type, handleUpdateURL);
  yield takeEvery(deleteURL.type, handleDeleteURL);
  yield takeEvery(fetchFrequency.type, handleFetchFrequency);
  yield takeEvery(fetchCategory.type, handleFetchCategory);
  yield takeEvery(fetchDetailURL.type, handleFetchDetailURL);
  yield takeEvery(deleteSummary.type, handleDeleteSummaryCrawling);
  yield takeEvery(updateFrequency.type, handleUpdateFrequency);
}
